import * as fabric from 'fabric'
import { WIDGET_ACCENT_COLORS, WIDGET_TYPES } from '../constants.js'
import { getWidgetDisplayText, decodeGifFrames, getGifFrameAt } from '../widgets/widgetRenderers.js'
import { FA_ICONS_BY_NAME } from '../faIconList.js'
import { computeOverlayAnimationTransform } from './overlayAnimation.js'

// Hashtag/mention/place render with a real colored icon glyph next to the label (not an emoji
// baked into the text string) - echoing the Story Maker's own sticker look (storyMaker/stiker/
// hashtag.vue, mention.vue, localisation.vue) far more closely than a plain "#tag"/"@user" string
// ever could. The other widget types (datetime/weather/music) keep their existing plain-text
// rendering - buildIconNode/layoutIconContent already exist for text's own prefix/suffix icon
// feature, reused here rather than duplicated.
const WIDGET_ICON_BY_TYPE = {
    [WIDGET_TYPES.HASHTAG]: 'hashtag',
    [WIDGET_TYPES.MENTION]: 'at',
    [WIDGET_TYPES.PLACE]: 'location-dot'
}

// The bare label these three widget types show next to their own icon - getWidgetDisplayText's
// own emoji/#/@-prefixed string is right for compact places (the timeline row's own label) but
// would double up with the real icon glyph here.
const getWidgetPillLabel = (overlay) => {
    const data = overlay.data || {}
    switch (overlay.widgetType) {
        case WIDGET_TYPES.HASHTAG: return (data.tag || 'hashtag').replace(/^#/, '')
        case WIDGET_TYPES.MENTION: return (data.username || 'utilisateur').replace(/^@/, '')
        case WIDGET_TYPES.PLACE: return data.location || 'Lieu'
        default: return getWidgetDisplayText(overlay) || 'Widget'
    }
}

const SELECTION_ACCENT = '#b64985'

// A plain `new fabric.Group(children)` defaults to Fabric v7's FitContentLayout manager, which
// unconditionally recalculates the group's own bounding box (and re-centers/shifts its children)
// on every single moving/modified/scaling/rotating trigger it sees bubble through - not just once
// at construction. Every group built in this file has its background/padding/icon layout already
// computed deliberately (see createWidgetPill/wrapTextWithChrome/layoutIconContent) - an
// after-the-fact recompute doesn't know about that intent and can land on a subtly different
// bounding box (stroke width, sub-pixel text metrics...), which is what showed up as a widget
// pill looking flattened with uneven padding after being dragged. FixedLayout keeps whatever
// layout was true at construction and never revisits it.
const createFrozenGroup = (children) => new fabric.Group(children, { layoutManager: new fabric.LayoutManager(new fabric.FixedLayout()) })

// A small circular badge with a hand-drawn curved-arrow icon, instead of Fabric's plain dot -
// immediately readable as "rotate" at a glance, the same idea as the Story Maker's own corner
// controls using distinct directional icons rather than a bare circle.
const renderRotateIcon = (ctx, left, top, styleOverride, fabricObject) => {
    const size = 22
    ctx.save()
    ctx.translate(left, top)
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle || 0))

    ctx.beginPath()
    ctx.arc(0, 0, size / 2, 0, Math.PI * 2)
    ctx.fillStyle = '#fff'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.25)'
    ctx.shadowBlur = 4
    ctx.fill()
    ctx.shadowColor = 'transparent'
    ctx.lineWidth = 1.5
    ctx.strokeStyle = SELECTION_ACCENT
    ctx.stroke()

    const r = size * 0.22
    const startAngle = fabric.util.degreesToRadians(-40)
    const endAngle = fabric.util.degreesToRadians(220)
    ctx.lineWidth = 1.6
    ctx.strokeStyle = SELECTION_ACCENT
    ctx.beginPath()
    ctx.arc(0, 0, r, startAngle, endAngle)
    ctx.stroke()

    const ax = Math.cos(endAngle) * r
    const ay = Math.sin(endAngle) * r
    ctx.beginPath()
    ctx.moveTo(ax, ay)
    ctx.lineTo(ax - 3.5, ay - 1)
    ctx.lineTo(ax - 1, ay + 3.5)
    ctx.closePath()
    ctx.fillStyle = SELECTION_ACCENT
    ctx.fill()

    ctx.restore()
}

// Moves the rotation handle from Fabric's default spot (floating above top-center) to the
// top-left corner instead, echoing the Story Maker's sticker corner controls
// (storyMaker/draggable.vue's corner-rotate zones).
const moveRotationControlToCorner = (obj) => {
    obj.controls.mtr = new fabric.Control({
        x: -0.5,
        y: -0.5,
        offsetX: -16,
        offsetY: -16,
        cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
        actionHandler: fabric.controlsUtils.rotationWithSnapping,
        actionName: 'rotate',
        render: renderRotateIcon
    })
}

// Fabric's default selection chrome (small gray squares, thin dashed border) reads as dated next
// to the rest of the editor - round, accent-bordered handles and a solid accent outline instead,
// in the same spirit as Story Maker's own polished corner controls.
const applySelectionStyle = (obj) => {
    obj.set({
        cornerStyle: 'circle',
        cornerColor: '#fff',
        cornerStrokeColor: SELECTION_ACCENT,
        cornerSize: 12,
        transparentCorners: false,
        borderColor: SELECTION_ACCENT,
        borderScaleFactor: 1.5,
        padding: 6,
        // Fabric caches a Group's rendered bitmap and only repaints it when it thinks the cache
        // is dirty - a plain move doesn't dirty a nested Group/Rect/Textbox's cache the same way
        // a full rebuild does, which is what left a widget's pill looking visually "condensed"
        // after being dragged even though its underlying transform data never actually changed
        // (a rebuild - e.g. editing the text - constructs a brand new object and so always
        // painted correctly). Every overlay here is small and there are only ever a handful on
        // screen, so always re-rendering from scratch is a negligible cost for guaranteed
        // correctness.
        objectCaching: false
    })
}

// Border/shadow are user-configurable (per overlay, via data.border*/shadow*) on top of a
// sensible fallback per shape ("pill" widgets default to a fully-rounded, accent-bordered look
// with a soft shadow already on; plain text defaults to square corners, no border, no shadow -
// each matching what used to be hardcoded before these became adjustable).
const buildShadow = (data, defaultEnabled) => {
    const enabled = data.shadowEnabled !== undefined ? data.shadowEnabled : defaultEnabled
    if (!enabled) return null
    return new fabric.Shadow({
        color: data.shadowColor || 'rgba(0,0,0,0.3)',
        blur: data.shadowBlur !== undefined ? data.shadowBlur : 10,
        offsetX: data.shadowOffsetX !== undefined ? data.shadowOffsetX : 0,
        offsetY: data.shadowOffsetY !== undefined ? data.shadowOffsetY : 2
    })
}

// Loads an <img> element for a user-uploaded image overlay - a plain Promise wrapper since
// createFabricObjectForOverlay itself is async (see below) specifically to allow this.
const loadImageElement = (url) => new Promise((resolve) => {
    if (!url) { resolve(null); return }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = url
})

// A prefix/suffix icon (data.prefixIcon/suffixIcon) can be '' (none), a plain emoji string
// (legacy format from before the FontAwesome option existed), or { type: 'emoji'|'fa', value }.
// Either way it renders as its own Fabric object next to the text - not embedded as a character
// in the Textbox string - since a vector FA glyph has no text-string representation at all (only
// a real emoji character could ever be embedded that way). Returns null for "no icon".
const buildIconNode = (iconRef, fontSize, color) => {
    if (!iconRef) return null
    const ref = typeof iconRef === 'string' ? { type: 'emoji', value: iconRef } : iconRef

    if (ref.type === 'fa') {
        const def = FA_ICONS_BY_NAME[ref.value]
        if (!def) return null
        const path = new fabric.Path(def.path, { fill: color, originX: 'center', originY: 'center', objectCaching: false })
        // FA glyphs are authored on a viewBox roughly proportional to their own path bounds, not
        // the font's em-square - scaling by the path's own rendered height (rather than assuming
        // a fixed viewBox size) keeps every icon visually consistent next to the text regardless
        // of its individual proportions.
        const targetHeight = fontSize * 0.8
        if (path.height) path.scale(targetHeight / path.height)
        return path
    }

    return new fabric.FabricText(ref.value, { originX: 'center', originY: 'center', fontSize, fill: color, objectCaching: false })
}

// True when the overlay has at least one prefix/suffix icon attached - text WITH an icon is laid
// out as a Group (see layoutIconContent) whose width/position depends on both the text AND the
// icon(s), unlike plain text which is a lightweight standalone Textbox. Exported so
// OverlayCanvas.vue can route width-slider-driven resizes for these through a full rebuild
// instead of the cheap in-place resync path (see its contentSignature).
export const hasIconContent = (overlay) => {
    if (!overlay || overlay.type !== 'text') return false
    const data = overlay.data || {}
    return !!data.prefixIcon || !!data.suffixIcon
}

// Lays out [prefixNode, textbox, suffixNode] left-to-right with a small gap, centered as a whole
// around the group's local origin (0,0) - the same "centered at 0,0" convention used everywhere
// else in this file (background rects, widget pills) so the result composes naturally into a
// fabric.Group. Returns the combined { width, height } since Fabric doesn't recompute a Group's
// own cached size when a member is repositioned after construction.
const layoutIconContent = (prefixNode, textbox, suffixNode, gap) => {
    const parts = [prefixNode, textbox, suffixNode].filter(Boolean)
    const widths = parts.map(p => p.width * (p.scaleX || 1))
    const totalWidth = widths.reduce((sum, w) => sum + w, 0) + gap * (parts.length - 1)

    let cursor = -totalWidth / 2
    parts.forEach((p, i) => {
        p.set({ left: cursor + widths[i] / 2, top: 0 })
        cursor += widths[i] + gap
    })

    const totalHeight = Math.max(...parts.map(p => p.height * (p.scaleY || 1)))
    return { width: totalWidth, height: totalHeight }
}

// Independent top/right/bottom/left margins (px) around a piece of content, each falling back to
// its own default when not explicitly set by the user - lets the background grow unevenly around
// the content instead of a single uniform margin on every side.
const resolvePadding = (data, fallbackX, fallbackY) => ({
    top: data.paddingTop !== undefined ? data.paddingTop : fallbackY,
    right: data.paddingRight !== undefined ? data.paddingRight : fallbackX,
    bottom: data.paddingBottom !== undefined ? data.paddingBottom : fallbackY,
    left: data.paddingLeft !== undefined ? data.paddingLeft : fallbackX
})

// Widgets render as a rounded "pill" sticker (white background + icon/label) rather than plain
// text, echoing the Story Maker's sticker look (storyMaker/stiker/localisation.vue, mention.vue) -
// those DOM-based stickers auto-size to their own content (a <p> in a padded flex box), which a
// fixed widthPct box can't replicate: "Paris, France" and "@x" are wildly different lengths, so a
// one-size-fits-all background was either cropping long labels or leaving text overflowing an
// oversized-relative-to-it pill. Width is instead derived from the actual measured text.
const createWidgetPill = (overlay, width, height) => {
    const data = overlay.data || {}
    const accent = WIDGET_ACCENT_COLORS[overlay.widgetType] || '#2f1937'
    const label = getWidgetPillLabel(overlay)
    const fontSize = Math.max(14, height * 0.42)
    // Vertical padding defaults to 0 - the pill's height is already the overlay's own fixed box
    // height, unlike text where the box grows to fit whatever font size is chosen.
    const padding = resolvePadding(data, height * 0.6, 0)

    const text = new fabric.FabricText(label, {
        originX: 'center',
        originY: 'center',
        fontSize,
        fontFamily: 'Poppins',
        fontWeight: '700',
        fill: data.textColor || '#1a1a1a',
        objectCaching: false
    })

    // hashtag/mention/place get their own real icon glyph (accent-colored, same as the Story
    // Maker's own fixed per-type icon color) laid out next to the label via the same
    // buildIconNode/layoutIconContent pair text's own prefix-icon feature already uses - other
    // widget types have no icon glyph defined and fall back to the label alone, exactly as before.
    const iconName = WIDGET_ICON_BY_TYPE[overlay.widgetType]
    const iconNode = iconName ? buildIconNode({ type: 'fa', value: iconName }, fontSize, accent) : null
    const content = iconNode ? layoutIconContent(iconNode, text, null, fontSize * 0.35) : { width: text.width, height: text.height }

    // Still respects a manually widened box (widthPct dragged larger than the content needs), only
    // grows past it when the content itself wouldn't fit.
    const pillWidth = Math.max(width, content.width + padding.left + padding.right)
    const pillHeight = height + padding.top + padding.bottom
    const radiusPct = data.borderRadiusPct !== undefined ? data.borderRadiusPct : 1
    const borderWidth = data.borderWidth !== undefined ? data.borderWidth : 2

    // Uneven left/right (or top/bottom) padding means the pill's own center no longer coincides
    // with the content's center - the background is offset by half the difference between its two
    // sides instead, while the content itself stays anchored at the group's local origin (0,0).
    const offsetX = (padding.right - padding.left) / 2
    const offsetY = (padding.bottom - padding.top) / 2

    // Fabric v6 defaults every object's origin to "center" (not "left"/"top" like v5) - without
    // an explicit origin here, `left/top: -half size` was being read as the rect's *center*,
    // shifting the whole background pill up-left by half its own size relative to the content
    // (which does declare originX/Y: center) instead of the two sharing the same center. That
    // mismatch is exactly what made the label always look offset from its pill.
    const background = new fabric.Rect({
        left: offsetX,
        top: offsetY,
        width: pillWidth,
        height: pillHeight,
        rx: radiusPct * (pillHeight / 2),
        ry: radiusPct * (pillHeight / 2),
        originX: 'center',
        originY: 'center',
        fill: data.bgColor || '#ffffff',
        stroke: borderWidth > 0 ? (data.borderColor || accent) : null,
        strokeWidth: borderWidth,
        // Without this, resizing the widget (which scales the whole group, including this rect)
        // scales the stroke thickness right along with it - non-uniformly if the user drags a
        // side handle rather than a corner, since scaleX/scaleY can then differ. That's what made
        // the border look inconsistent/"not following" after a resize. strokeUniform keeps the
        // rendered border a constant width regardless of how the object gets scaled.
        strokeUniform: true,
        shadow: buildShadow(data, true),
        objectCaching: false
    })

    return createFrozenGroup([background, iconNode, text].filter(Boolean))
}

// Text gets the same optional chrome as widgets (background/border/radius/shadow) - only wrapped
// in a Group with a backing rect when at least one of those is actually configured, so a plain
// caption stays a lightweight Textbox exactly as before. `content` is either the plain Textbox,
// or (when a prefix/suffix icon is attached) the Group produced by layoutIconContent - either
// way its own .width/.height already reflect what needs wrapping; fontSize is passed separately
// since a Group has no .fontSize of its own to fall back on.
const wrapTextWithChrome = (content, data, fontSize) => {
    const padding = resolvePadding(data, Math.max(8, fontSize * 0.3), Math.max(6, fontSize * 0.25))
    const radiusPct = data.borderRadiusPct !== undefined ? data.borderRadiusPct : 0
    const borderWidth = data.borderWidth !== undefined ? data.borderWidth : 0
    const bgWidth = content.width + padding.left + padding.right
    const bgHeight = content.height + padding.top + padding.bottom
    // Same asymmetric-padding offset as the widget pill above - the content stays put at (0,0),
    // the background shifts by half the left/right (or top/bottom) difference to wrap around it.
    const offsetX = (padding.right - padding.left) / 2
    const offsetY = (padding.bottom - padding.top) / 2

    // No background = nothing for a rect shadow to cast, so shadow falls back to the text/icon itself.
    const hasBackground = !!data.backgroundColor

    const background = new fabric.Rect({
        left: offsetX,
        top: offsetY,
        width: bgWidth,
        height: bgHeight,
        originX: 'center',
        originY: 'center',
        fill: data.backgroundColor || 'transparent',
        rx: radiusPct * (bgHeight / 2),
        ry: radiusPct * (bgHeight / 2),
        stroke: borderWidth > 0 ? (data.borderColor || '#000000') : null,
        strokeWidth: borderWidth,
        strokeUniform: true,
        shadow: hasBackground ? buildShadow(data, false) : null,
        objectCaching: false
    })

    if (!hasBackground) {
        content.set({ shadow: buildShadow(data, false), objectCaching: false })
    }

    return createFrozenGroup([background, content])
}

// Pure helper shared by the interactive OverlayCanvas.vue (live editing) and the offline export
// pipeline (OfflineOverlayRenderer below) so the two never drift out of sync visually. Async
// because the 'image' overlay type has to actually load the uploaded file before it can be
// wrapped in a fabric object - every other type resolves on the same tick as before.
export const createFabricObjectForOverlay = async (overlay, canvasSize) => {
    const { transform, data, type } = overlay
    const left = transform.xPct * canvasSize.width
    const top = transform.yPct * canvasSize.height
    const width = transform.widthPct * canvasSize.width
    const height = transform.heightPct * canvasSize.height

    let obj = null

    if (type === 'widget' && overlay.widgetType === 'gif') {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        obj = new fabric.FabricImage(canvas, { left, top, originX: 'center', originY: 'center' })
    } else if (type === 'widget') {
        obj = createWidgetPill(overlay, width, height)
        obj.set({ left, top, originX: 'center', originY: 'center' })
        // Side handles (ml/mr/mt/mb) let scaleX and scaleY end up different from each other -
        // for a pill/sticker widget that just stretches it into an oval and (even with
        // strokeUniform) makes the rounded-corner border look wrong. Corner handles only, so a
        // resize always stays uniform.
        obj.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false })
    } else if (type === 'image') {
        const img = await loadImageElement(data && data.imageUrl)
        if (img && img.width) {
            obj = new fabric.FabricImage(img, { left, top, originX: 'center', originY: 'center' })
            const lockRatio = transform.lockAspectRatio !== false
            if (lockRatio || !transform.heightPct) {
                // Preserves the image's own aspect ratio (scaleToWidth keeps scaleX/scaleY
                // equal) - a photo/logo squished to an arbitrary, independently-chosen box
                // ratio would just look wrong, so this is the default unless explicitly unlocked.
                obj.scaleToWidth(width)
            } else {
                // Unlocked: width and height are each driven by their own independent percentage,
                // free to stretch the image away from its native ratio.
                obj.set({ scaleX: width / img.width, scaleY: height / img.height })
            }
            // Locked ratio only needs corner handles (any of them already scales uniformly) -
            // unlocked also exposes the side handles so a single axis can be stretched on its own.
            obj.setControlsVisibility({ ml: !lockRatio, mr: !lockRatio, mt: !lockRatio, mb: !lockRatio })
        }
    } else if (type === 'text') {
        const fontSize = (data && data.fontSize) || 42
        const textColor = (data && data.color) || '#FFFFFF'
        const textbox = new fabric.Textbox((data && data.text) || 'Texte', {
            width,
            fontSize,
            fontFamily: (data && data.fontFamily) || 'Poppins',
            fontWeight: data && data.bold ? 'bold' : 'normal',
            fontStyle: data && data.italic ? 'italic' : 'normal',
            underline: !!(data && data.underline),
            linethrough: !!(data && data.strikethrough),
            fill: textColor,
            textAlign: (data && data.align) || 'center',
            originX: 'center',
            originY: 'center',
            objectCaching: false,
            // Fabric defaults Textbox to editable:true, which lets a click on an already-active
            // one enter its own native inline editing - this app always edits text via the custom
            // double-click -> floating <input> flow (onCanvasDoubleClick), never Fabric's own.
            editable: false
        })

        const prefixNode = data && buildIconNode(data.prefixIcon, fontSize, textColor)
        const suffixNode = data && buildIconNode(data.suffixIcon, fontSize, textColor)
        let content = textbox
        if (prefixNode || suffixNode) {
            layoutIconContent(prefixNode, textbox, suffixNode, fontSize * 0.25)
            content = createFrozenGroup([prefixNode, textbox, suffixNode].filter(Boolean))
        }

        const hasChrome = data && (data.backgroundColor || data.borderWidth || data.shadowEnabled)
        obj = hasChrome ? wrapTextWithChrome(content, data, fontSize) : content
        obj.set({ left, top })
        // Disables the mid-side handles: they'd scale a single axis, but the data model only
        // stores one uniform `scale`, so a non-uniform drag got silently reverted on next sync.
        if (obj === textbox) obj.setControlsVisibility({ mt: false, mb: false })
        else obj.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false })
    } else if (type === 'shape') {
        const shape = (data && data.shape) || 'rectangle'
        const fill = (data && data.color) || 'rgba(255,255,255,0.6)'
        if (shape === 'circle') {
            obj = new fabric.Circle({ left, top, radius: width / 2, fill, originX: 'center', originY: 'center' })
        } else if (shape === 'oval') {
            obj = new fabric.Ellipse({ left, top, rx: width / 2, ry: height / 2, fill, originX: 'center', originY: 'center' })
        } else if (shape === 'triangle') {
            obj = new fabric.Triangle({ left, top, width, height, fill, originX: 'center', originY: 'center' })
        } else if (shape === 'line') {
            // A thick-stroked, round-capped straight line rather than a filled shape - "color"
            // still means whatever's visibly colored, just via stroke instead of fill here.
            const strokeWidth = Math.max(4, height * 0.25)
            obj = new fabric.Line([-width / 2, 0, width / 2, 0], {
                left, top, stroke: fill, strokeWidth, strokeLineCap: 'round', originX: 'center', originY: 'center'
            })
        } else {
            obj = new fabric.Rect({ left, top, width, height, fill, originX: 'center', originY: 'center' })
        }
    }

    if (!obj) return null

    // Images size themselves via scaleToWidth() above (kept in sync with transform.widthPct,
    // not the generic "scale" field every other overlay type uses) - applying scale here too
    // would silently stomp that back to transform.scale's default of 1 (the image's own native,
    // unscaled resolution) immediately after scaleToWidth had just set it correctly.
    if (type === 'image') {
        obj.set({ angle: transform.rotationDeg || 0 })
    } else {
        obj.set({
            angle: transform.rotationDeg || 0,
            scaleX: transform.scale || 1,
            scaleY: transform.scale || 1
        })
    }
    obj.data = { overlayId: overlay.id }
    applySelectionStyle(obj)
    moveRotationControlToCorner(obj)
    return obj
}

// Canvas blur via ctx.filter, scoped to this object by temporarily overriding its render method -
// Fabric's own image filters only work on fabric.Image, not text/group/shape.
export const setObjectBlur = (obj, blurPx) => {
    if (blurPx > 0.5) {
        if (!obj.__originalRender) obj.__originalRender = obj.render.bind(obj)
        obj.__blurPx = blurPx
        obj.render = function (ctx) {
            ctx.save()
            ctx.filter = `blur(${this.__blurPx}px)`
            this.__originalRender(ctx)
            ctx.restore()
        }
    } else if (obj.__originalRender) {
        obj.render = obj.__originalRender
        delete obj.__originalRender
        delete obj.__blurPx
    }
}

// Non-interactive, framework-free counterpart to OverlayCanvas.vue used by the export pipeline:
// it can render the overlay layer at any arbitrary timestamp, synchronously in a for-loop,
// without needing Vue/DOM reactivity to settle between frames.
export class OfflineOverlayRenderer {
    constructor(canvasSize) {
        this.canvasSize = canvasSize
        this.canvas = document.createElement('canvas')
        this.canvas.width = canvasSize.width
        this.canvas.height = canvasSize.height
        this.fabricCanvas = new fabric.StaticCanvas(this.canvas, {
            width: canvasSize.width,
            height: canvasSize.height,
            backgroundColor: 'transparent'
        })
        this.objectsByOverlayId = new Map()
        this.gifDataByOverlayId = new Map()
    }

    async prepare(overlays) {
        for (const overlay of overlays) {
            const obj = await createFabricObjectForOverlay(overlay, this.canvasSize)
            if (!obj) continue
            // Captured once so per-frame animation offsets apply on top of this base instead of compounding.
            obj.__baseLeft = obj.left
            obj.__baseTop = obj.top
            obj.__baseScaleX = obj.scaleX || 1
            obj.__baseScaleY = obj.scaleY || 1
            obj.__baseAngle = obj.angle || 0
            this.objectsByOverlayId.set(overlay.id, obj)
            this.fabricCanvas.add(obj)

            if (overlay.widgetType === 'gif' && overlay.data && overlay.data.gifUrl) {
                try {
                    const gifData = await decodeGifFrames(overlay.data.gifUrl)
                    this.gifDataByOverlayId.set(overlay.id, gifData)
                } catch (e) { /* gif not loadable - overlay stays blank */ }
            }
        }
    }

    renderAt(overlays, atMs) {
        overlays.forEach((overlay) => {
            const obj = this.objectsByOverlayId.get(overlay.id)
            if (!obj) return
            const active = atMs >= overlay.startMs && atMs <= overlay.endMs
            obj.visible = active
            if (active) {
                const anim = computeOverlayAnimationTransform(overlay, atMs)
                obj.set({
                    opacity: anim.opacity,
                    left: obj.__baseLeft + anim.offsetXPct * this.canvasSize.width,
                    top: obj.__baseTop + anim.offsetYPct * this.canvasSize.height,
                    scaleX: obj.__baseScaleX * anim.scale,
                    scaleY: obj.__baseScaleY * anim.scale,
                    angle: obj.__baseAngle + anim.rotationDeg
                })
                obj.setCoords()
                setObjectBlur(obj, anim.blurPx)
                const gifData = this.gifDataByOverlayId.get(overlay.id)
                if (gifData) {
                    const frameCanvas = getGifFrameAt(gifData, atMs - overlay.startMs)
                    if (frameCanvas) obj.setElement(frameCanvas)
                }
            }
        })
        this.fabricCanvas.renderAll()
        return this.canvas
    }

    dispose() {
        this.fabricCanvas.dispose()
    }
}
